/**
 * WebSocket Service for Real-time Updates
 * Manages Socket.IO connection and event handling
 */

import { io, Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';
import CrossTabSync from './CrossTabSync';
import { performGlobalLogout } from '@/config/firebase';
import { getAuthTokenWithActivity } from '@/utils/apiUtils';
import { getApiBaseUrl } from '@/utils/apiBase';
import { AuthManager } from '@/contexts/AuthManager';
import { devLog, devWarn, devError } from '@/utils/devLog';
import { debounceQueryInvalidation } from './socketDebouncer';
import type {
  FileMovedPayload,
  FileSoftDeletedPayload,
  FileRestoredPayload,
  FolderCreatedPayload,
  FolderRenamedPayload,
  FolderMovedPayload,
  FolderUnwrappedPayload,
  FolderSoftDeletedPayload,
  FolderRestoredPayload,
  AutoOrganizedPayload,
} from '@shared/dataroom/socketEventTypes';

interface SocketServiceConfig {
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

// FIX #9: Typed event map for WebSocket events
// This provides type safety for socket event handlers
export interface SocketEventMap {
  // Connection events
  'socket:connected': { socketId: string | undefined };
  'socket:disconnected': { reason: string };
  'socket:error': { error: unknown };
  'socket:reconnected': { attempts: number };
  'socket:pong': unknown;

  // Quant compute completion events (identifiers only; consumers validate before use)
  'quant:run-done': { projectId?: string; jobId?: string; op?: string; runId?: string | null; runKey?: string | null };
  'quant:job-done': { projectId?: string; jobId?: string; op?: string };

  // Chat events
  'chat:message': {
    sessionId: string;
    message: {
      role: 'user' | 'assistant';
      content: string;
      timestamp: number;
      citations?: Array<{
        title?: string;
        snippet: string;
        source?: string;
        confidence?: number;
        pageNumber?: number;
      }>;
    };
  };

  // Project events
  'project:created': { projectId: string; title?: string; userId?: string };
  'project:updated': { projectId: string; changes?: Record<string, unknown> };
  'project:deleted': { projectId: string };
  'project:approved': { projectId: string };
  'project:rejected': { projectId: string };
  'project:pending': { projectId: string };
  'project:thumbnailUpdated': { projectId: string; thumbnailUrl?: string };
  'projects:visibility:changed': { projectIds?: string[] };
  'projects:status:changed': { projectIds?: string[] };
  'projects:updated': { projectIds?: string[] };

  // Edit lock & inline editing events
  'project:editLockAcquired': {
    projectId: string;
    userId: string;
    userName: string;
    lockedAt: string; // ISO 8601
  };
  'project:editLockReleased': {
    projectId: string;
    userId: string;
    reason: 'save' | 'discard' | 'timeout' | 'force_release';
  };
  'project:edited': {
    projectId: string;
    userId: string;
    userName: string;
    changeLogId: string;
    changeCount: number;
  };

  // Corpus events
  'corpus:updated': { projectId: string };
  'corpus:created': { projectId: string };
  'corpus:imported': { projectId: string };

  // Organization events - include both orgId (UUID) and organization name for backward compatibility
  'organization:created': { organizationId: string; organizationName?: string };
  'organization:deleted': { organizationId: string; organizationName?: string };
  'organization:updated': { organizationId?: string; organizationName?: string; organization?: string };
  'organization:member:added': { organizationId?: string; organizationName?: string; organization?: string; userId: string };
  'organization:member:removed': { organizationId?: string; organizationName?: string; organization?: string; userId: string };

  // Dataroom events
  'dataroom:fileAdded': { projectId: string; fileId: string };
  'dataroom:fileDeleted': { projectId: string; fileId: string };
  'dataroom:metadataUpdated': {
    projectId: string;
    fileId?: string;
    updatedBy?: string;
    timestamp?: number;
  };

  // Dataroom file-system events (Phase 4 — DMS VDR file-system feature)
  // Payload contracts live in `@shared/dataroom/socketEventTypes` so backend
  // emitters and frontend consumers share a single source of truth.
  'dataroom:fileMoved': FileMovedPayload;
  'dataroom:fileSoftDeleted': FileSoftDeletedPayload;
  'dataroom:fileRestored': FileRestoredPayload;
  'dataroom:folderCreated': FolderCreatedPayload;
  'dataroom:folderRenamed': FolderRenamedPayload;
  'dataroom:folderMoved': FolderMovedPayload;
  'dataroom:folderUnwrapped': FolderUnwrappedPayload;
  'dataroom:folderSoftDeleted': FolderSoftDeletedPayload;
  'dataroom:folderRestored': FolderRestoredPayload;
  'dataroom:autoOrganized': AutoOrganizedPayload;

  // Note events
  'note:created': { noteId: string; projectId?: string };
  'note:updated': { noteId: string };
  'note:deleted': { noteId: string };

  // User events
  'user:approved': { userId: string; userData?: Record<string, unknown> };
  'user:rejected': { userId: string; userData?: Record<string, unknown> };
  'user:updated': { userId: string; userData?: Record<string, unknown> };
  'user:deleted': { userId: string };
  'account:deleted': { reason: string };
  'profile:updated': { userId: string; organizationChanged?: boolean };

  // Notification events
  'notification:created': { notificationId: string; userId?: string };
  'notification:read': { notificationId: string };
  'notification:deleted': { notificationId: string };
  'notification:new': { notificationId: string; userId?: string };

  // State events
  'state:reconciled': Record<string, unknown>;
  'cache:invalidate': { queryKey: unknown[] };

  // Share events.
  //
  // The payload is the project id and nothing else. These events say only "what you are
  // showing for this project is out of date" — the client then refetches through the REST
  // endpoints, which authorize per caller and so return exactly what THIS user may see.
  //
  // The particulars (shareRequestId, targetType, targetId, fromOrganizationId) used to be
  // carried here and were removed at the source: these events are delivered to organization
  // ROOMS as well as to the individual recipient, so naming the target told every member of
  // the issuing organization which outside person had just been granted or lost access to a
  // deal. No handler ever read those fields — they all key off `projectId` — so declaring
  // them here now would promise data the server does not send and invite exactly that.
  'share:created': { projectId: string };
  'share:accepted': { projectId: string };
  'share:declined': { projectId: string };
  'share:revoked': { projectId: string };
  'share:cancelled': { projectId: string };
  'share:expired': { projectId: string };

  // Access-revocation events. Emitted directly to a specific socket (not a room
  // broadcast) the moment a share or organization-membership revocation commits —
  // see `kickUserFromProjectRoom`, `kickUserFromDataroomRoom`,
  // `kickOrganizationFromProjectRoom`, and `kickUserFromOrganizationRoom` in the
  // backend socketService. The project-scoped kicks send `{ projectId, reason }`;
  // the organization-membership kick sends `{ organizationId, reason }` instead —
  // the union below is the real wire shape, not a superset of it.
  'access:revoked': { projectId: string; reason: string } | { organizationId: string; reason: string };

  // Knowledge Repository events
  'knowledge:processing-complete': { orgId: string; fileIds: string[]; status: string };
  'knowledge:deal-processing-complete': { orgId: string; dealIds: string[]; status: string };
  'knowledge:deal-synthesis-complete': { orgId: string; dealIds: string[] };
  /**
   * Emitted by the Phase 2 debounce worker the moment it claims a deal for
   * auto-resynthesis (after a new file has been associated to an existing deal
   * and the debounce window has elapsed). The frontend listens for this to
   * show a "re-synthesizing" banner in the deal detail panel and to disable
   * the manual "Reprocess deal" button while the in-flight synthesis runs.
   */
  'knowledge:deal-resynthesizing': { orgId: string; dealId: string };
  /**
   * Per-file status transition emitted by the Phase 1 transactional outbox
   * dispatcher. Carries a monotonic `sequenceNumber` per `(orgId, batchId)`
   * so the frontend can drop stale events and invalidate only the affected
   * file + its containing deal list.
   */
  'knowledge:file-status-updated': {
    orgId: string;
    fileId: string;
    status: string;
    batchId: string;
    sequenceNumber: number;
    /** Optional — present when the file is currently associated to a deal. */
    dealId?: string | null;
  };

  // Portfolio events
  'portfolio:holding:created': { orgId: string; holdingId?: string; projectId?: string; status?: string; timestamp: number };
  'portfolio:holding:updated': { orgId: string; holdingId?: string; projectId?: string; timestamp: number };
  'portfolio:holding:deleted': { orgId: string; holdingId?: string; timestamp: number };
  'portfolio:holding:extraction_complete': { orgId: string; holdingId?: string; projectId?: string; confidenceScore?: number; timestamp: number };
  'portfolio:aggregates:updated': { orgId: string; timestamp: number };
  'portfolio:nav:updated': { orgId: string; timestamp: number };
  'portfolio:cashflow:added': { orgId: string; holdingId?: string; cashFlowId?: string; type?: string; amount?: number; timestamp: number };
  'portfolio:cashflow:deleted': { orgId: string; cashFlowId?: string; timestamp: number };

  // Chat stream completion events (background agent finished)
  'chat:stream-complete': { sessionId: string; projectId: string; status: 'completed' | 'error' | 'timeout' };

  // Fallback for untyped events
  [key: string]: unknown;
}

// Generic socket event data type (for backward compatibility)
interface SocketEventData {
  [key: string]: unknown;
}

// Type for event callbacks with type inference
type EventCallback<K extends keyof SocketEventMap = string> = (data: SocketEventMap[K]) => void;

class SocketService {
  private static instance: SocketService | null = null;
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private eventCallbacks: Map<string, Set<EventCallback>> = new Map();
  private config: SocketServiceConfig;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private authUnsubscribe: (() => void) | null = null;
  private currentToken: string | null = null;
  private authManagerUnsubscribe: (() => void) | null = null;
  private isReconnecting: boolean = false;
  // H5 FIX: Track pending room subscriptions so we can (re)subscribe on connect/reconnect
  private pendingOrgSubs: Set<string> = new Set();
  private pendingProjectSubs: Set<string> = new Set();
  private pendingDataroomSubs: Set<string> = new Set();
  // Track cross-tab event listeners for cleanup
  private crossTabListeners: Array<{ event: string; handler: (e: Event) => void }> = [];
  private isInitialized: boolean = false;

  constructor(config: SocketServiceConfig = {}) {
    // Prevent re-initialization
    if (this.isInitialized) {
      devLog('[SocketService] Already initialized, skipping constructor');
      return;
    }

    this.config = {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      ...config
    };

    // PHASE 2 FIX: Initialize CrossTabSync for leader election
    const crossTabSync = CrossTabSync.getInstance();

    // Only initialize connection if we're the leader tab
    if (this.config.autoConnect) {
      // Check if we should connect based on leader status
      if (crossTabSync.getIsLeader()) {
        this.initAuthListener();
      } else {
        // Listen for socket events from leader tab
        this.setupCrossTabEventListeners();
      }
    }

    this.isInitialized = true;
  }

  /**
   * PHASE 2: Singleton pattern for SocketService
   */
  public static getInstance(config?: SocketServiceConfig): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService(config || {});
    }
    return SocketService.instance;
  }

  /**
   * PHASE 2 FIX: Setup listeners for cross-tab socket events
   */
  private setupCrossTabEventListeners(): void {
    // Clear any existing listeners first
    this.cleanupCrossTabListeners();

    // Listen for socket events from the leader tab via BroadcastChannel
    const socketEventHandler = (event: CustomEvent) => {
      const { event: socketEvent, data } = event.detail;
      // Emit to local listeners as if we received it from socket
      this.emitToListeners(socketEvent, data);
    };
    window.addEventListener('socket-event', socketEventHandler as EventListener);
    this.crossTabListeners.push({ event: 'socket-event', handler: socketEventHandler as EventListener });

    // Listen for cache invalidation events
    const cacheInvalidationHandler = (event: CustomEvent) => {
      const { queryKey } = event.detail;
      // Trigger cache invalidation locally
      this.emitToListeners('cache:invalidate', { queryKey });
    };
    window.addEventListener('cache-invalidation', cacheInvalidationHandler as EventListener);
    this.crossTabListeners.push({ event: 'cache-invalidation', handler: cacheInvalidationHandler as EventListener });

    devLog('[SocketService] Setup cross-tab event listeners (follower mode)');
  }

  /**
   * Clean up cross-tab event listeners
   */
  private cleanupCrossTabListeners(): void {
    this.crossTabListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.crossTabListeners = [];
  }

  /**
   * Initialize authentication state listener
   */
  private initAuthListener(): void {
    // Clean up any existing auth listener first
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }

    const auth = getAuth();
    this.authUnsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // User is signed in, connect to WebSocket
        // Use cached token from AuthManager - proactive refresh handled centrally
        const token = await getAuthTokenWithActivity(false);
        if (!this.isConnected && !this.socket?.connected) {
          // FIX: Convert null to undefined for type compatibility
          this.connect(token ?? undefined);
        }
      } else {
        // User is signed out, disconnect WebSocket
        this.disconnect();
      }
    });

    // Subscribe to AuthManager token updates for proactive refresh
    const authManager = AuthManager.getInstance();
    this.authManagerUnsubscribe = authManager.subscribe((authState) => {
      try {
        // Handle logout - disconnect socket when user logs out
        if (!authState.user || !authState.authToken) {
          if (this.socket?.connected) {
            devLog('[SocketService] User logged out, disconnecting socket');
            this.disconnect();
          }
          this.currentToken = null;
          return;
        }

        // Handle initial token (cold start) - just store it
        if (!this.currentToken) {
          devLog('[SocketService] Storing initial token from AuthManager');
          this.currentToken = authState.authToken;
          return;
        }

        // Handle token refresh - only if token actually changed
        if (this.currentToken !== authState.authToken) {
          devLog('[SocketService] Detected token refresh from AuthManager');
          devLog('[SocketService] Old token:', this.currentToken.substring(0, 20) + '...');
          devLog('[SocketService] New token:', authState.authToken.substring(0, 20) + '...');

          this.currentToken = authState.authToken;

          // Proactively reconnect with new token if socket is currently connected
          // Check socket.connected only (not this.isConnected which may be stale)
          // Also check socket is not in disconnecting state
          if (this.socket?.connected && !this.isReconnecting) {
            devLog('[SocketService] Proactively reconnecting with fresh token...');

            // Set reconnection guard to prevent concurrent reconnections
            this.isReconnecting = true;

            // Update socket auth before reconnecting
            this.socket.auth = { token: authState.authToken };

            // Wait for clean disconnect before reconnecting
            this.socket.once('disconnect', () => {
              devLog('[SocketService] Disconnected, now reconnecting with fresh token');
              if (this.socket) {
                this.socket.connect();
              }
            });

            // Reset reconnection guard when connection completes
            this.socket.once('connect', () => {
              devLog('[SocketService] Proactive reconnection complete');
              this.isReconnecting = false;
            });

            // Also reset on error to prevent getting stuck
            this.socket.once('connect_error', () => {
              devError('[SocketService] Reconnection failed, resetting guard');
              this.isReconnecting = false;
            });

            // Disconnect to trigger reconnect
            this.socket.disconnect();
          } else if (this.isReconnecting) {
            devLog('[SocketService] Reconnection already in progress, skipping');
          } else {
            devLog('[SocketService] Socket not connected, skipping reconnection');
          }
        }
      } catch (error) {
        devError('[SocketService] Error in AuthManager subscription:', error);
        // Reset reconnection guard on any error
        this.isReconnecting = false;
      }
    });

    devLog('[SocketService] Subscribed to AuthManager token updates');
  }

  /**
   * Connect to WebSocket server
   */
  async connect(token?: string): Promise<void> {
    // Prevent duplicate connections
    if (this.isConnected || this.socket?.connected) {
      devLog('[SocketService] WebSocket already connected, skipping connection');
      return;
    }

    // Prevent connection if socket is being created
    if (this.socket && !this.socket.connected) {
      devLog('[SocketService] Socket exists but not connected, cleaning up first');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Get token if not provided
    if (!token) {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        devError('No authenticated user for WebSocket connection');
        return;
      }
      // Use cached token from AuthManager - proactive refresh handled centrally
      // FIX: Convert null to undefined for type compatibility
      token = (await getAuthTokenWithActivity(false)) ?? undefined;
    }

    this.currentToken = token ?? null;

    // SECURITY FIX: Determine backend URL based on environment - always prefer HTTPS
    // Remove HTTP fallback to prevent protocol downgrade attacks
    // Phase 4 close-out: the API origin comes from the shared resolver (env-aware);
    // the literal production host that used to sit here as a fallback is gone.
    const backendUrl = import.meta.env.VITE_WEBSOCKET_URL || getApiBaseUrl();

    // Create Socket.IO connection
    this.socket = io(backendUrl, {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      reconnection: this.config.reconnection,
      reconnectionAttempts: this.config.reconnectionAttempts,
      reconnectionDelay: this.config.reconnectionDelay,
      timeout: 10000
    });

    this.setupEventHandlers();
  }

  /**
   * Setup Socket.IO event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      devLog('WebSocket connected:', this.socket?.id);
      this.isConnected = true;
      this.emitToListeners('socket:connected', { socketId: this.socket?.id });

      // H5 FIX: Re-subscribe to all pending rooms on connect
      this.resubscribeAllPendingRooms();
    });

    this.socket.on('disconnect', (reason) => {
      devLog('WebSocket disconnected:', reason);
      this.isConnected = false;
      this.emitToListeners('socket:disconnected', { reason });
    });

    this.socket.on('error', async (error: unknown) => {
      devError('WebSocket error:', error);

      // Check if error is authentication-related
      const errorMsg = error?.toString().toLowerCase() || '';
      const isAuthError =
        errorMsg.includes('unauthorized') ||
        errorMsg.includes('authentication failed') ||
        errorMsg.includes('invalid token') ||
        errorMsg.includes('token expired');

      if (isAuthError) {
        devError('[Socket] Authentication error detected');

        // Check if we just received a new token from AuthManager
        // If so, skip refresh and just reconnect
        const authManager = AuthManager.getInstance();
        const currentAuthToken = authManager.getCurrentState().authToken;

        if (currentAuthToken && currentAuthToken !== this.currentToken) {
          devLog('[Socket] AuthManager already has fresh token, using it for reconnection');
          this.currentToken = currentAuthToken;
          this.disconnect();
          await this.connect(currentAuthToken);
          return;
        }

        // Try token refresh before disconnecting
        try {
          devLog('[Socket] Attempting token refresh...');
          await getAuthTokenWithActivity(true);

          // Refresh succeeded - reconnect socket with new token
          devLog('[Socket] Token refresh successful - reconnecting...');
          this.disconnect();
          const newToken = await getAuthTokenWithActivity(false);
          if (newToken) {
            await this.connect(newToken);
            return; // Don't emit error if we recovered
          }
        } catch (refreshError) {
          devError('[Socket] Token refresh failed:', refreshError);
        }

        // Refresh failed - logout
        devLog('[Socket] Triggering logout due to unrecoverable auth error');
        await performGlobalLogout();
      }

      this.emitToListeners('socket:error', { error });
    });

    // Reconnection events
    this.socket.on('reconnect', (attemptNumber) => {
      devLog('WebSocket reconnected after', attemptNumber, 'attempts');
      this.emitToListeners('socket:reconnected', { attempts: attemptNumber });

      // H5 FIX: Re-subscribe to all tracked rooms on reconnect
      this.resubscribeAllPendingRooms();

      // Request state synchronization if available
      try {
        this.socket?.emit('state:sync', {
          lastConnected: Date.now() - (attemptNumber * 1000),
          requestedScopes: ['projects', 'organizations', 'notifications']
        });
      } catch (error: unknown) {
        devError('Error during reconnection setup:', error);
      }
    });

    this.socket.on('reconnect_error', (error: unknown) => {
      devError('WebSocket reconnection error:', error);
    });

    // Real-time data events
    this.setupDataEventHandlers();

    // Ping-pong for connection health
    this.socket.on('pong', (data) => {
      this.emitToListeners('socket:pong', data);
    });

    // State reconciliation response handler
    this.socket.on('state:reconciled', (data) => {
      devLog('State reconciliation completed:', data);
      this.emitToListeners('state:reconciled', data);
    });
  }

  /**
   * H5 FIX: Re-subscribe to all tracked rooms after connect/reconnect
   */
  private resubscribeAllPendingRooms(): void {
    try {
      // Re-subscribe to organization rooms
      this.pendingOrgSubs.forEach(org => {
        this.socket?.emit('subscribe:organization', org);
        this.broadcastToTabs('room-join', `organization:${org}`);
      });

      // Re-subscribe to project rooms
      this.pendingProjectSubs.forEach(projectId => {
        this.socket?.emit('subscribe:project', projectId);
        this.broadcastToTabs('room-join', `project:${projectId}`);
      });

      // Re-subscribe to dataroom rooms
      this.pendingDataroomSubs.forEach(projectId => {
        this.socket?.emit('subscribe:dataroom', projectId);
        this.broadcastToTabs('room-join', `dataroom:${projectId}`);
      });
    } catch {
      // Intentionally ignored - room subscription failures are non-critical
    }
  }

  /**
   * Setup handlers for real-time data events
   */
  private setupDataEventHandlers(): void {
    if (!this.socket) return;

    // Quant completion pushes. Cache invalidation is centralized in queryClient; this layer only
    // forwards the raw payload into the typed/cross-tab listener pipe.
    this.socket.on('quant:run-done', (data) => {
      this.emitToListeners('quant:run-done', data);
    });

    this.socket.on('quant:job-done', (data) => {
      this.emitToListeners('quant:job-done', data);
    });

    // Project events
    this.socket.on('project:created', (data) => {
      this.emitToListeners('project:created', data);
    });

    this.socket.on('project:updated', (data) => {
      this.emitToListeners('project:updated', data);
    });

    this.socket.on('project:deleted', (data) => {
      this.emitToListeners('project:deleted', data);
    });

    this.socket.on('project:approved', (data) => {
      this.emitToListeners('project:approved', data);
    });

    this.socket.on('project:rejected', (data) => {
      this.emitToListeners('project:rejected', data);
    });

    // Project moved back to pending (admin action)
    this.socket.on('project:pending', (data) => {
      this.emitToListeners('project:pending', data);
    });

    this.socket.on('project:thumbnailUpdated', (data) => {
      this.emitToListeners('project:thumbnailUpdated', data);
    });

    // Edit lock & inline editing events
    this.socket.on('project:editLockAcquired', (data) => {
      this.emitToListeners('project:editLockAcquired', data);
    });

    this.socket.on('project:editLockReleased', (data) => {
      this.emitToListeners('project:editLockReleased', data);
    });

    this.socket.on('project:edited', (data) => {
      this.emitToListeners('project:edited', data);
    });

    // Organization events
    this.socket.on('organization:created', (data) => {
      this.emitToListeners('organization:created', data);
    });

    this.socket.on('organization:deleted', (data) => {
      this.emitToListeners('organization:deleted', data);
    });

    this.socket.on('organization:member:added', (data) => {
      this.emitToListeners('organization:member:added', data);
    });

    this.socket.on('organization:member:removed', (data) => {
      this.emitToListeners('organization:member:removed', data);
    });

    this.socket.on('organization:updated', (data) => {
      this.emitToListeners('organization:updated', data);
    });

    // Dataroom events
    this.socket.on('dataroom:fileAdded', (data) => {
      this.emitToListeners('dataroom:fileAdded', data);
    });

    this.socket.on('dataroom:fileDeleted', (data) => {
      this.emitToListeners('dataroom:fileDeleted', data);
    });

    this.socket.on('dataroom:metadataUpdated', (data) => {
      this.emitToListeners('dataroom:metadataUpdated', data);
    });

    // Dataroom file-system events (Phase 4 — DMS VDR file-system feature)
    //
    // Each handler does two things, in order:
    //   1. Forwards the event to local listeners (UI components that have
    //      registered via `socketService.on(...)` to react to the event
    //      directly — e.g. a toast or in-tree highlight).
    //   2. Schedules a debounced TanStack Query invalidation against every
    //      query key that depends on the touched data. The debouncer
    //      collapses bursts (e.g. folder unwrap fans out N file-moves)
    //      and respects suppression flags set by in-flight mutations.
    //
    // Query-key mapping is documented in spec §5.4 and mirrored here so
    // downstream changes can audit it without leaving this file.
    this.setupDataroomFsHandlers();

    // Note events
    this.socket.on('note:created', (data) => {
      this.emitToListeners('note:created', data);
    });

    this.socket.on('note:updated', (data) => {
      this.emitToListeners('note:updated', data);
    });

    this.socket.on('note:deleted', (data) => {
      this.emitToListeners('note:deleted', data);
    });

    // User events (admin operations)
    this.socket.on('user:approved', (data) => {
      this.emitToListeners('user:approved', data);
    });

    this.socket.on('user:rejected', (data) => {
      this.emitToListeners('user:rejected', data);
    });

    this.socket.on('user:updated', (data) => {
      this.emitToListeners('user:updated', data);
    });

    this.socket.on('user:deleted', (data) => {
      this.emitToListeners('user:deleted', data);
    });

    // User profile events
    this.socket.on('profile:updated', (data) => {
      this.emitToListeners('profile:updated', data);
    });

    // Notification events
    this.socket.on('notification:created', (data) => {
      this.emitToListeners('notification:created', data);
    });

    this.socket.on('notification:read', (data) => {
      this.emitToListeners('notification:read', data);
    });

    this.socket.on('notification:deleted', (data) => {
      this.emitToListeners('notification:deleted', data);
    });

    // Notification events
    this.socket.on('notification:new', (data) => {
      this.emitToListeners('notification:new', data);
    });

    // Project status change events (approval, etc)
    this.socket.on('projects:visibility:changed', (data) => {
      this.emitToListeners('projects:visibility:changed', data);
    });

    this.socket.on('projects:status:changed', (data) => {
      this.emitToListeners('projects:status:changed', data);
    });

    this.socket.on('projects:updated', (data) => {
      this.emitToListeners('projects:updated', data);
    });

    // Share events
    this.socket.on('share:created', (data) => {
      this.emitToListeners('share:created', data);
    });

    this.socket.on('share:accepted', (data) => {
      this.emitToListeners('share:accepted', data);
    });

    this.socket.on('share:declined', (data) => {
      this.emitToListeners('share:declined', data);
    });

    this.socket.on('share:revoked', (data) => {
      this.emitToListeners('share:revoked', data);
    });

    this.socket.on('share:cancelled', (data) => {
      this.emitToListeners('share:cancelled', data);
    });

    this.socket.on('share:expired', (data) => {
      this.emitToListeners('share:expired', data);
    });

    // Access-revocation events. The backend emits this straight to the affected
    // socket (see the SocketEventMap entry above for the emit sites and payload
    // shapes) — bridge it into the typed listener pipe the same way every other
    // raw event above is bridged, so `queryClient`'s registered handler actually
    // runs and the caller's dataroom/project caches get invalidated immediately.
    this.socket.on('access:revoked', (data) => {
      this.emitToListeners('access:revoked', data);
    });

    // Account deletion is terminal for the authenticated identity. The backend emits this
    // directly to each of the deleted user's sockets immediately before disconnecting it.
    // Bridge it for observability, then clear all local/cross-tab auth state; merely waiting
    // for the transport disconnect would leave a valid-looking deleted session rendered.
    this.socket.on('account:deleted', (data) => {
      this.emitToListeners('account:deleted', data);
      void performGlobalLogout().catch((error: unknown) => {
        devError('[Socket] Failed to complete logout after account deletion:', error);
      });
    });

    // Portfolio events
    const portfolioEvents = [
      'portfolio:holding:created',
      'portfolio:holding:updated',
      'portfolio:holding:deleted',
      'portfolio:holding:extraction_complete',
      'portfolio:aggregates:updated',
      'portfolio:nav:updated',
      'portfolio:cashflow:added',
      'portfolio:cashflow:deleted',
    ] as const;

    for (const event of portfolioEvents) {
      this.socket.on(event, (data) => {
        this.emitToListeners(event, data);
      });
    }

    // Knowledge Repository events
    this.socket.on('knowledge:processing-complete', (data) => {
      this.emitToListeners('knowledge:processing-complete', data);
    });

    this.socket.on('knowledge:deal-processing-complete', (data) => {
      this.emitToListeners('knowledge:deal-processing-complete', data);
    });

    this.socket.on('knowledge:deal-synthesis-complete', (data) => {
      this.emitToListeners('knowledge:deal-synthesis-complete', data);
    });

    this.socket.on('knowledge:deal-resynthesizing', (data) => {
      this.emitToListeners('knowledge:deal-resynthesizing', data);
    });

    this.socket.on('knowledge:file-status-updated', (data) => {
      this.emitToListeners('knowledge:file-status-updated', data);
    });

    // Chat stream completion events (background agent finished)
    this.socket.on('chat:stream-complete', (data) => {
      this.emitToListeners('chat:stream-complete', data);
    });

    // Chat message events (real-time message delivery)
    this.socket.on('chat:message', (data) => {
      this.emitToListeners('chat:message', data);
    });
  }

  /**
   * Subscribe to the 10 dataroom file-system events introduced in Phase 4
   * of the DMS VDR file-system feature. Each handler:
   *
   *   1. Forwards the event to local listeners (UI subscribers).
   *   2. Calls `debounceQueryInvalidation` against every TanStack Query
   *      key that depends on the affected data. The debouncer collapses
   *      bursts and respects per-key suppression flags from in-flight
   *      mutations (so optimistic updates aren't clobbered).
   *
   * Mapping (per spec §5.4):
   *
   *   fileMoved          → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *   fileSoftDeleted    → ['dataroom', pid] + ['dataroom', pid, 'recently-deleted']
   *   fileRestored       → ['dataroom', pid] + ['dataroom', pid, 'recently-deleted']
   *   folderCreated      → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *   folderRenamed      → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *   folderMoved        → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *   folderUnwrapped    → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *   folderSoftDeleted  → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *                        + ['dataroom', pid, 'recently-deleted']
   *   folderRestored     → ['dataroom', pid] + ['dataroom', pid, 'folders']
   *                        + ['dataroom', pid, 'recently-deleted']
   *   autoOrganized      → ['dataroom', pid] + ['dataroom', pid, 'folders']
   */
  private setupDataroomFsHandlers(): void {
    if (!this.socket) return;

    this.socket.on('dataroom:fileMoved', (data) => {
      this.emitToListeners('dataroom:fileMoved', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });

    this.socket.on('dataroom:fileSoftDeleted', (data) => {
      this.emitToListeners('dataroom:fileSoftDeleted', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['recently-deleted']);
    });

    this.socket.on('dataroom:fileRestored', (data) => {
      this.emitToListeners('dataroom:fileRestored', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['recently-deleted']);
    });

    this.socket.on('dataroom:folderCreated', (data) => {
      this.emitToListeners('dataroom:folderCreated', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });

    this.socket.on('dataroom:folderRenamed', (data) => {
      this.emitToListeners('dataroom:folderRenamed', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });

    this.socket.on('dataroom:folderMoved', (data) => {
      this.emitToListeners('dataroom:folderMoved', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });

    this.socket.on('dataroom:folderUnwrapped', (data) => {
      this.emitToListeners('dataroom:folderUnwrapped', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });

    this.socket.on('dataroom:folderSoftDeleted', (data) => {
      this.emitToListeners('dataroom:folderSoftDeleted', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders', 'recently-deleted']);
    });

    this.socket.on('dataroom:folderRestored', (data) => {
      this.emitToListeners('dataroom:folderRestored', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders', 'recently-deleted']);
    });

    this.socket.on('dataroom:autoOrganized', (data) => {
      this.emitToListeners('dataroom:autoOrganized', data);
      this.invalidateDataroomFsKeys(data?.projectId, ['folders']);
    });
  }

  /**
   * Helper for the dataroom file-system handlers: schedules debounced
   * invalidations for the project's base dataroom key plus any number of
   * sub-keys (e.g. 'folders', 'recently-deleted').
   *
   * Guards against missing projectId (defensive: malformed payloads must
   * not crash the socket pipe).
   */
  private invalidateDataroomFsKeys(
    projectId: string | undefined,
    subKeys: ReadonlyArray<'folders' | 'recently-deleted'>,
  ): void {
    if (!projectId) return;
    debounceQueryInvalidation(['dataroom', projectId]);
    for (const sub of subKeys) {
      debounceQueryInvalidation(['dataroom', projectId, sub]);
    }
  }

  /**
   * Broadcast room subscription changes to other tabs
   * Only used for specific events that need cross-tab mirroring
   */
  private broadcastToTabs(eventType: 'room-join' | 'room-leave', room: string): void {
    // Only broadcast if we're the leader tab
    const crossTabSync = CrossTabSync.getInstance();
    if (!crossTabSync.getIsLeader()) return;

    // Use CrossTabSync's broadcast method to notify other tabs
    crossTabSync.broadcast('socket-room-change', {
      action: eventType,
      room: room
    });
  }

  /**
   * Subscribe to a project room
   */
  subscribeToProject(projectId: string): void {
    // H5 FIX: Track subscription for reconnect
    if (projectId) this.pendingProjectSubs.add(projectId);

    if (!this.socket?.connected) {
      devLog('[socketService] Queuing subscription — WebSocket not yet connected');
      return;
    }

    this.socket.emit('subscribe:project', projectId);
    // Notify other tabs about room subscription
    this.broadcastToTabs('room-join', `project:${projectId}`);
  }

  /**
   * Unsubscribe from a project room
   */
  unsubscribeFromProject(projectId: string): void {
    // H5 FIX: Remove from tracked subscriptions
    if (projectId) this.pendingProjectSubs.delete(projectId);

    if (!this.socket?.connected) return;

    this.socket.emit('unsubscribe:project', projectId);
    // Notify other tabs about room unsubscription
    this.broadcastToTabs('room-leave', `project:${projectId}`);
  }

  /**
   * Subscribe to a dataroom
   */
  subscribeToDataroom(projectId: string): void {
    // H5 FIX: Track subscription for reconnect
    if (projectId) this.pendingDataroomSubs.add(projectId);

    if (!this.socket?.connected) {
      devWarn('Cannot subscribe: WebSocket not connected');
      return;
    }

    this.socket.emit('subscribe:dataroom', projectId);
    // Notify other tabs about room subscription
    this.broadcastToTabs('room-join', `dataroom:${projectId}`);
  }

  /**
   * Unsubscribe from a dataroom
   */
  unsubscribeFromDataroom(projectId: string): void {
    // H5 FIX: Remove from tracked subscriptions
    if (projectId) this.pendingDataroomSubs.delete(projectId);

    if (!this.socket?.connected) return;

    this.socket.emit('unsubscribe:dataroom', projectId);
    // Notify other tabs about room unsubscription
    this.broadcastToTabs('room-leave', `dataroom:${projectId}`);
  }

  /**
   * Subscribe to an organization room (uses UUID)
   */
  subscribeToOrganization(organizationId: string): void {
    // Track desired subscription
    if (organizationId) this.pendingOrgSubs.add(organizationId);
    if (!this.socket?.connected) {
      // Defer emit until connected; no warning to avoid console noise
      return;
    }
    this.socket.emit('subscribe:organization', organizationId);
    this.broadcastToTabs('room-join', `organization:${organizationId}`);
  }

  /**
   * Unsubscribe from an organization (uses UUID)
   */
  unsubscribeFromOrganization(organizationId: string): void {
    // Remove from pending set
    if (organizationId) this.pendingOrgSubs.delete(organizationId);
    if (!this.socket?.connected) return;
    this.socket.emit('unsubscribe:organization', organizationId);
    this.broadcastToTabs('room-leave', `organization:${organizationId}`);
  }

  /**
   * Emit an event to the server (alias for compatibility)
   */
  emit(event: string, data: SocketEventData): void {
    this.emitToServer(event, data);
  }

  /**
   * Emit an event to the server
   */
  emitToServer(event: string, data: SocketEventData): void {
    if (!this.socket?.connected) {
      devWarn('Cannot emit: WebSocket not connected');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Register event listener
   */
  on(event: string, callback: EventCallback): void {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, new Set());
    }
    this.eventCallbacks.get(event)?.add(callback);
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: EventCallback): void {
    this.eventCallbacks.get(event)?.delete(callback);
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.eventCallbacks.delete(event);
    } else {
      this.eventCallbacks.clear();
    }
  }

  /**
   * Emit event to registered local listeners
   */
  private emitToListeners(event: string, data: SocketEventData): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error: unknown) {
          devError(`Error in event callback for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Send ping to check connection health
   */
  ping(): void {
    if (this.socket?.connected) {
      this.socket.emit('ping');
    }
  }

  /**
   * Disconnect from WebSocket server
   *
   * FIX M5: Only tear down the socket transport; do NOT clear eventCallbacks.
   * App-level listeners registered via .on() must survive disconnect/reconnect
   * cycles. Callbacks are only wiped in cleanup() (full teardown) or via
   * explicit removeAllListeners() calls.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;

    // FIX M5: eventCallbacks are intentionally preserved across disconnect/reconnect.
    // Components register listeners once and expect them to survive reconnection.
    // Clearing them here caused components to silently lose all event handlers
    // after any transient disconnect, requiring a full page reload to recover.

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    devLog('[SocketService] Cleaning up all resources');

    // Unsubscribe from AuthManager
    if (this.authManagerUnsubscribe) {
      devLog('[SocketService] Unsubscribing from AuthManager token updates');
      this.authManagerUnsubscribe();
      this.authManagerUnsubscribe = null;
    }

    // Disconnect socket
    this.disconnect();

    // FIX M5: Clear eventCallbacks only during full cleanup (not on disconnect)
    this.eventCallbacks.clear();

    // Clean up auth listener
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }

    // Clean up cross-tab listeners
    this.cleanupCrossTabListeners();

    // Clear pending subscriptions
    this.pendingOrgSubs.clear();
    this.pendingProjectSubs.clear();
    this.pendingDataroomSubs.clear();

    // Reset initialization flag
    this.isInitialized = false;

    // Clear singleton instance
    SocketService.instance = null;
  }

  /**
   * Get connection status
   */
  getStatus(): {
    connected: boolean;
    socketId: string | undefined;
  } {
    return {
      connected: this.isConnected && this.socket?.connected === true,
      socketId: this.socket?.id
    };
  }

  /**
   * Check if connected
   */
  get connected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Get the raw socket instance (for debugging)
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Export singleton instance using getInstance() to ensure proper initialization
export const socketService = SocketService.getInstance();

// Export class for testing
export { SocketService };
